import React, { useState } from "react";
import { createPortal } from "react-dom";
import {
  X, Loader2, Eye, EyeOff, Check, User, Mail, Phone, KeyRound, ShieldCheck, IdCard,
} from "lucide-react";
import { createAdminUser, ADMIN_CREATABLE_USER_TYPE_OPTIONS } from "../../services/adminUsersService";

const MOBILE_RE = /^[6789]\d{9}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PASSWORD_RULES = [
  { test: (v) => v.length >= 8, label: "At least 8 characters" },
  { test: (v) => /[A-Z]/.test(v), label: "One uppercase letter" },
  { test: (v) => /[a-z]/.test(v), label: "One lowercase letter" },
  { test: (v) => /[0-9]/.test(v), label: "One number" },
  { test: (v) => /[#?!@$%^&*-]/.test(v), label: "One special character (#?!@$%^&*-)" },
];

const EMPTY = {
  username: "", first_name: "", last_name: "", email: "",
  mobile: "", user_type: "wallet_ops", user_role: "", password: "",
};

const inputCls =
  "w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50";
const Label = ({ children, required }) => (
  <label className="block text-sm font-medium text-gray-700 mb-1.5">
    {children}{required && <span className="text-red-500"> *</span>}
  </label>
);

const CreateAdminUserModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  if (!open) return null;

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
    setApiError("");
  };

  const passwordOk = PASSWORD_RULES.every((r) => r.test(form.password));

  const validate = () => {
    const e = {};
    if (!form.username.trim()) e.username = "Username is required.";
    if (!form.first_name.trim()) e.first_name = "First name is required.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!EMAIL_RE.test(form.email.trim())) e.email = "Enter a valid email address.";
    if (form.mobile.trim() && !MOBILE_RE.test(form.mobile.trim())) {
      e.mobile = "Enter a valid 10-digit mobile number.";
    }
    if (!ADMIN_CREATABLE_USER_TYPE_OPTIONS.some((o) => o.value === form.user_type)) {
      e.user_type = "Select a role.";
    }
    if (!passwordOk) e.password = "Password does not meet the requirements below.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setApiError("");
    try {
      const payload = {
        username: form.username.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        user_type: form.user_type,
        password: form.password,
      };
      if (form.mobile.trim()) payload.mobile = form.mobile.trim();
      if (form.user_role.trim()) payload.user_role = form.user_role.trim();

      const resp = await createAdminUser(payload);
      onCreated?.(resp?.msg || null);
      setForm(EMPTY);
      setErrors({});
      onClose();
    } catch (err) {
      setApiError(err.message || "Failed to create admin user.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    if (submitting) return;
    setForm(EMPTY);
    setErrors({});
    setApiError("");
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 leading-tight">Create Admin User</h3>
              <p className="text-xs text-gray-400">Provision a new Admin Panel account</p>
            </div>
          </div>
          <button onClick={close} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto">
          {apiError && (
            <div className="p-3 rounded-xl border text-sm bg-red-50 border-red-200 text-red-700 font-medium">
              {apiError}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label required>First name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input className={inputCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="Enter first name" />
              </div>
              {errors.first_name && <p className="mt-1 text-xs text-red-500">{errors.first_name}</p>}
            </div>
            <div>
              <Label>Last name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input className={inputCls} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder="Enter last name" />
              </div>
            </div>
          </div>

          <div>
            <Label required>Username</Label>
            <div className="relative">
              <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input className={inputCls} value={form.username} onChange={(e) => set("username", e.target.value)}
                autoCapitalize="none" autoCorrect="off" placeholder="Enter username" />
            </div>
            {errors.username && <p className="mt-1 text-xs text-red-500">{errors.username}</p>}
            <p className="mt-1 text-xs text-gray-400">Used to sign in. Must be unique.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label required>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input className={inputCls} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Enter email" />
              </div>
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
            </div>
            <div>
              <Label>Mobile</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input className={inputCls} inputMode="numeric" maxLength={10} value={form.mobile}
                  onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))} placeholder="Enter mobile number" />
              </div>
              {errors.mobile && <p className="mt-1 text-xs text-red-500">{errors.mobile}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label required>Role</Label>
              <div className="relative">
                <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
                <select className={`${inputCls} appearance-none`} value={form.user_type} onChange={(e) => set("user_type", e.target.value)}>
                  {ADMIN_CREATABLE_USER_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {errors.user_type && <p className="mt-1 text-xs text-red-500">{errors.user_type}</p>}
            </div>
            <div>
              <Label>Designation</Label>
              <div className="relative">
                <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input className={inputCls} value={form.user_role} onChange={(e) => set("user_role", e.target.value)} placeholder="e.g. Ops Analyst" />
              </div>
              <p className="mt-1 text-xs text-gray-400">Optional label for future RBAC.</p>
            </div>
          </div>

          <div>
            <Label required>Password</Label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input className={`${inputCls} pr-10`} type={showPassword ? "text" : "password"}
                value={form.password} onChange={(e) => set("password", e.target.value)}
                autoComplete="new-password" placeholder="Set a strong password" />
              <button type="button" onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" aria-label="Toggle password visibility">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password}</p>}
            {form.password && (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                {PASSWORD_RULES.map((r) => {
                  const ok = r.test(form.password);
                  return (
                    <div key={r.label} className={`flex items-center gap-1.5 text-xs ${ok ? "text-emerald-600" : "text-gray-400"}`}>
                      <Check className={`w-3.5 h-3.5 ${ok ? "opacity-100" : "opacity-30"}`} />
                      {r.label}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button type="button" onClick={close} disabled={submitting}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={submitting}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 shadow-sm flex items-center gap-2 disabled:opacity-60">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? "Creating…" : "Create Admin User"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateAdminUserModal;
